import React from "react"
import { CheckSquare, ArrowRight, AlertTriangle, Clock, Loader2 } from "lucide-react"
import { Link } from "react-router-dom"
import { cn } from "src/utils/cn"
import { useMyDay } from "../hooks/useMyDay"

function MiniTask({ task }) {
  const isOverdue = task.overdue
  return (
    <div className={cn(
      "flex items-center gap-2 rounded-lg px-3 py-2",
      isOverdue ? "bg-red-50" : "bg-slate-50"
    )}>
      {isOverdue
        ? <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-red-500" />
        : <Clock className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
      }
      <span className={cn("min-w-0 flex-1 truncate text-[13px]", isOverdue ? "text-red-700 font-medium" : "text-slate-700")}>
        {task.title}
      </span>
      {task.due_at && (
        <span className={cn("flex-shrink-0 text-[11px]", isOverdue ? "text-red-500" : "text-slate-400")}>
          {new Date(task.due_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </span>
      )}
    </div>
  )
}

export function MyDayWidget() {
  const { buckets, loading } = useMyDay()
  const overdue = buckets.overdue.slice(0, 3)
  const today   = buckets.today.slice(0, 3 - overdue.length)
  const items   = [...overdue, ...today]
  const totalOpen = buckets.overdue.length + buckets.today.length

  return (
    <div className="relative flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
      <div className="absolute inset-x-0 top-0 h-[3px] rounded-t-xl" style={{ backgroundColor: "#3b82f6" }} />
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CheckSquare className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-semibold text-slate-800">My Day</span>
          {totalOpen > 0 && (
            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-700">
              {totalOpen}
            </span>
          )}
        </div>
        <Link to="/my-day" className="flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-700 font-medium">
          View all <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-slate-300" />
          </div>
        ) : items.length === 0 ? (
          <p className="py-4 text-center text-[13px] text-slate-400">All caught up! ✓</p>
        ) : (
          <div className="space-y-1.5">
            {items.map((t) => <MiniTask key={t.id} task={t} />)}
            {totalOpen > items.length && (
              <Link to="/my-day" className="block rounded-lg px-3 py-2 text-center text-[12px] text-slate-400 hover:bg-slate-50 hover:text-slate-600">
                +{totalOpen - items.length} more
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
